import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { getMyOrders, OrderItem } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  paid: { label: "支付成功", className: "bg-green-50 text-green-600" },
  pending: { label: "待支付", className: "bg-yellow-50 text-yellow-600" },
  cancelled: { label: "已取消", className: "bg-red-50 text-red-600" },
};

export default function PaymentRecordsPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMsg({ type, message });
    setTimeout(() => setToastMsg(null), 3000);
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await getMyOrders();
      setOrders(data);
    } catch (e) {
      console.error('加载订单失败:', e);
      showToast('加载订单失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 计算VIP剩余天数
  const getVipRemainingDays = () => {
    if (!user?.vip_expires_at) return null;
    const expireDate = new Date(user.vip_expires_at);
    const now = new Date();
    const diff = expireDate.getTime() - now.getTime();
    if (diff <= 0) return null;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const remainingDays = getVipRemainingDays();
  const isVip = user?.isVip && remainingDays !== null;

  return (
    <DashboardLayout>
      <div className="p-5 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-lg md:text-xl font-bold text-gray-800">付款记录</h1>
        </div>

        {/* VIP Status Card - 仅VIP用户显示 */}
        {isVip && (
          <div className="bg-gradient-to-r from-orange-500 to-amber-400 rounded-2xl p-5 text-white mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-vip-crown-fill text-xl" />
                  <span className="font-bold text-lg">VIP会员</span>
                </div>
                <p className="text-white/80 text-sm">有效期至：{user?.vip_expires_at?.split(' ')[0] || '-'}</p>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-xs mb-1">剩余天数</p>
                <p className="text-3xl font-bold">{remainingDays}</p>
                <p className="text-white/70 text-xs">天</p>
              </div>
            </div>
          </div>
        )}

        {/* Records */}
        {loading ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line text-2xl text-gray-400 animate-spin" />
            <p className="text-gray-500 mt-2">加载中...</p>
          </div>
        ) : orders.length > 0 ? (
          <div className="space-y-3">
            {orders.map((record) => {
              const statusInfo = STATUS_MAP[record.status] || STATUS_MAP.pending;
              // 计算有效期
              const startDate = record.paid_at ? record.paid_at.split(' ')[0] : '-';
              const endDate = record.expire_at ? record.expire_at.split(' ')[0] : '-';
              return (
                <div key={record.id} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-800 text-sm">{record.plan_name}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {record.duration_months ? `${record.duration_months}个月套餐` : ''}
                      </p>
                    </div>
                    <span className="text-lg font-bold text-orange-500 shrink-0">¥{record.amount}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <i className="ri-calendar-line" />
                      下单时间：{record.created_at?.split(' ')[0] || '-'}
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      有效期：{startDate} 至 {endDate}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100">
              <i className="ri-receipt-line text-2xl text-gray-400" />
            </div>
            <p className="text-gray-500">暂无付款记录</p>
            <Link
              to="/vip"
              className="inline-flex items-center mt-4 px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors text-sm"
            >
              立即开通VIP
            </Link>
          </div>
        )}
      </div>

      {/* Toast */}
      {toastMsg && (
        <div
          className={`fixed bottom-20 left-1/2 -translate-x-1/2 px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 z-50 ${
            toastMsg.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
          }`}
        >
          <i className={`ri-${toastMsg.type === 'success' ? 'checkbox-circle' : 'error-warning'}-line`} />
          {toastMsg.message}
        </div>
      )}
    </DashboardLayout>
  );
}
